import React, { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { MapApi, MapApiEdit, GetQrLink } from "../../../redux/requests/map";
import { navigationStep } from "../../../redux/slices/navigation";
import Navigation from "../../../components/navigation";
import SecondScreen from "../../SecondScreen";
import StepCustomize from "../../../components/stepCustomize";
import QrTitleField from "../../../components/input/qrTitleField";
import { saveValueMap } from "../../../redux/slices/saveValue";
import { useSelector } from "react-redux";
import styled from "styled-components";
import "mapbox-gl/dist/mapbox-gl.css";
import { preloaderToggle } from "../../../redux/slices/preloader";
import axios from "axios";
import { isNoEditRoute } from "../../../hooks/helpers";
import { setCategory } from "../../../redux/slices/selectCategories";
import Form from "react-bootstrap/Form";
import InputText from "../../../components/input/inputText";
import { SelectCategory } from "../../../components";
import {
  validGoogleMap,
  validShortGoogleMap,
  validLinkPattern,
  validShortGoogleMap2,
} from "../../../RegExpValidations";
import MapIcon from "./svg/MapIcon";
import { MAX_LINK_LENGTH } from "../../../constants/inputValues";
import { MapContainer, TileLayer, Marker, useMapEvent } from "react-leaflet";
import L from "leaflet";
import iconUrl from "leaflet/dist/images/marker-icon.png";
import iconRetinaUrl from "leaflet/dist/images/marker-icon-2x.png";
import iconShadowUrl from "leaflet/dist/images/marker-shadow.png";
import "leaflet/dist/leaflet.css";
import useTypeDecription from "../../../hooks/useTypeDecription";
import useGetDataError from "../../../hooks/useGetDataError";
import useCurrentLocation from "../../../hooks/useCurrentLocation";

export const customIcon = L.icon({
  iconUrl: iconUrl,
  iconRetinaUrl: iconRetinaUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowUrl: iconShadowUrl,
  shadowSize: [41, 41],
  shadowAnchor: [12, 41],
});

export default function MapPage() {
  const { categoryChange, lang, qrLinkSliceId, valueMap, state, errorMsg } =
    useSelector((state) => ({
      categoryChange: state.categories.categoryChange,
      lang: state.changeLang.language.stepTwo.map,
      qrLinkSliceId: state.getQrLinkSlice.id,
      valueMap: state.saveValue.valueMap,
      state: state,
      errorMsg: state.changeLang.language.stepTwo.errors,
    }));
  const { id } = useParams();
  const dispatch = useDispatch();
  let navigate = useNavigate();
  const { content, getContent } = useTypeDecription();
  const { checkErrorStatus } = useGetDataError();
  const { adminEntryLocation } = useCurrentLocation();

  const mapRef = useRef(null);

  const [qrTitle, setQrTitle] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [error, setError] = useState(false);
  const [link, setLink] = useState("");
  const [validLink, setValidLink] = useState(false);

  useEffect(() => {
    getContent("map");
  }, []);

  useEffect(() => {
    if (id) {
      axios
        .get(`/entry/map/get/${id}`)
        .then(({ data }) => {
          data[0]?.longitude && setLongitude(data[0].longitude);
          data[0]?.latitude && setLatitude(data[0].latitude);
          setQrTitle(data.qrTitle);
          data[0]?.link && setLink(data[0].link);
          dispatch(setCategory(data.adsCategories));
          data[0]?.latitude &&
            data[0]?.longitude &&
            generateGoogleLink(data[0].latitude, data[0].longitude);
          setValidLink(true);
        })
        .catch((err) => {
          checkErrorStatus(err.response.status);
        });
    }
  }, [id]);

  useEffect(() => {
    setQrTitle(valueMap[0]);
    setLatitude(valueMap[1]);
    setLongitude(valueMap[2]);
    setLink(valueMap[3]);
    setValidLink(true);
  }, [valueMap]);

  function step() {
    setError(false);
    if (!qrLinkSliceId && !id) {
      dispatch(
        GetQrLink(() =>
          MapApi({
            qrTitle,
            latitude,
            longitude,
            category: categoryChange,
            link,
          })
        )
      );
    } else if (qrLinkSliceId || id) {
      dispatch(
        GetQrLink(() =>
          MapApiEdit({
            id: qrLinkSliceId || id,
            qrTitle,
            latitude,
            longitude,
            category: categoryChange,
            link,
          })
        )
      );
    }
    dispatch(preloaderToggle(true));
  }

  useEffect(() => {
    if (
      state.getQrLinkSlice.status === "success" &&
      !!state.getQrLinkSlice.link &&
      state.preloader.toggle
    ) {
      if (isNoEditRoute) {
        dispatch(navigationStep(3));
        dispatch(saveValueMap([qrTitle, latitude, longitude, link]));
        navigate("/qr");
      } else {
        window.location.href = adminEntryLocation;
      }
    } else if (state.getQrLinkSlice.status === "failed") {
      dispatch(preloaderToggle(false));
    }
  }, [state.getQrLinkSlice.status]);

  const handleLatitude = (e) => {
    let str = e.target.value;
    if (/^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?)$/gi.test(str) || !str) {
      setLatitude(str);
      generateGoogleLink(str, longitude);
    }
  };

  const handleLongitude = (e) => {
    const str = e.target.value;
    if (
      /^[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/gi.test(str) ||
      !str
    ) {
      setLongitude(str);
      generateGoogleLink(latitude, str);
    }
  };

  const flyToMarker = () => {
    if (longitude && latitude) {
      mapRef.current.setView([latitude, longitude], mapRef.current.getZoom(), {
        animate: true,
      });
    }
  };

  useEffect(() => {
    if (!link) {
      generateGoogleLink(valueMap[1], valueMap[2]);
    }
  }, [valueMap]);

  const generateGoogleLink = (lat, long) => {
    if (lat && long) {
      setValidLink(true);
      setLink(`https://www.google.com/maps/place/@${lat},${long},8z`);
    }
  };

  const handleGoogleLink = (e) => {
    let str = e.target.value.trim();
    if (validLinkPattern.test(str) && validGoogleMap.test(str)) {
      setValidLink(true);
      if (!new RegExp("^(http|https)").test(str)) {
        str = `https://${str}`;
      }
      const coords = e.target.value.match(validGoogleMap);
      setLatitude(coords[1]);
      setLongitude(coords[2]);
    } else if (
      validLinkPattern.test(str) &&
      (validShortGoogleMap.test(str) || validShortGoogleMap2.test(str))
    ) {
      setValidLink(true);
      setLatitude("");
      setLongitude("");
    } else {
      setValidLink(false);
    }
    setLink(str);
  };

  function LocationMarker() {
    const map = useMapEvent("click", (e) => {
      setLatitude(e.latlng.lat);
      setLongitude(e.latlng.lng);
      generateGoogleLink(e.latlng.lat, e.latlng.lng);
      map.setView(e.latlng, map.getZoom(), {
        animate: true,
      });
    });
    return !latitude && !longitude ? null : (
      <Marker position={[latitude, longitude]} icon={customIcon} />
    );
  }

  return (
    <>
      <Navigation />
      <SecondScreen>
        <Form className="col-12 col-lg-6 mx-auto">
          <InputText
            type="link"
            error={
              (!validLink && link.length > 5) || link.length > MAX_LINK_LENGTH
            }
            valid={
              validLink && link.length > 5 && link.length <= MAX_LINK_LENGTH
            }
            name="link"
            value={link}
            onChange={handleGoogleLink}
            placeholder={lang[5]}
            onBlur={flyToMarker}
            prefixIcon={<MapIcon />}
            popoverText={lang[6]}
          >
            {!validLink && link.length > 5 && (
              <Form.Text className="text-danger">{lang[7]}</Form.Text>
            )}
            {link.length > MAX_LINK_LENGTH && (
              <Form.Text className="text-danger">{errorMsg[0]}</Form.Text>
            )}
          </InputText>

          <InputText
            type="number"
            min={-90}
            max={90}
            step={0.1}
            error={error && !latitude}
            valid={latitude}
            name="latitude"
            value={latitude}
            onKeyDown={(e) => e.keyCode === 69 && e.preventDefault()}
            onChange={handleLatitude}
            placeholder={lang[0]}
            onBlur={flyToMarker}
            popoverText={lang[8]}
          >
            {error && !latitude && (
              <Form.Text className="text-danger">{lang[4]}</Form.Text>
            )}
          </InputText>

          <InputText
            type="number"
            error={error && !longitude}
            valid={longitude}
            name="longitude"
            value={longitude}
            onKeyDown={(e) => e.keyCode === 69 && e.preventDefault()}
            onChange={handleLongitude}
            placeholder={lang[2]}
            onBlur={flyToMarker}
            popoverText={lang[9]}
          >
            {error && !longitude && (
              <Form.Text className="text-danger">{lang[4]}</Form.Text>
            )}
          </InputText>

          <MapWrapper>
            <MapContainer
              center={[40.6699, -103.5917]}
              zoom={3}
              scrollWheelZoom={false}
              className="w-100 my-4"
              style={{ height: 400 }}
              ref={mapRef}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <LocationMarker />
            </MapContainer>
          </MapWrapper>

          <QrTitleField
            value={qrTitle}
            onChange={(e) => setQrTitle(e.target.value)}
          />
        </Form>
        <SelectCategory />
        <StepCustomize
          emulate={step}
          disabled={
            validShortGoogleMap.test(link) || validShortGoogleMap2.test(link)
              ? link.length > MAX_LINK_LENGTH
              : !longitude ||
                !latitude ||
                (link && !validLink) ||
                link.length > MAX_LINK_LENGTH
          }
        />
      </SecondScreen>
      {isNoEditRoute && content}
    </>
  );
}

export const MapWrapper = styled.div`
  .leaflet-bottom,
  .leaflet-top {
    z-index: 400;
  }
`;
